import { useCallback, useMemo } from 'react';
import type { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';
import slice from 'lodash/slice';
import dropRight from 'lodash/dropRight';
import some from 'lodash/some';
import { PositionAndSizeFromProps } from '../../utils/styledcomponents';
import type { PositionAndSizeProps } from '../../utils/styledcomponents';
import Image from './Image';
import { sumPoints } from '../../data/Score';
import { isEqualHighscoreEntry } from '../../data/Highscore';
import type { HighscoreEntry } from '../../data/Highscore';
import Assets from '../../assets/Assets';

const PointsType = styled.th`
	text-align: center;
`;

const Rank = styled.td`
	padding-left: 1em;
	padding-right: 0.2em;
	text-align: right;
	width: 0;

	&:not(:empty)::after {
		content: '.';
	}
`;

const Name = styled.td`
	max-width: 0;
	overflow: hidden;
	padding-left: 0.2em;
	padding-right: 1em;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const PointsValue = styled.td`
	padding-left: 1em;
	padding-right: 1em;
	text-align: center;
	width: 0;
`;

const CalculationSymbol = styled.td`
	padding-left: 0.2em;
	padding-right: 0.2em;
	text-align: center;
	width: 0;
`;

const SumPointsValue = styled.td`
	padding-left: 1em;
	padding-right: 1em;
	text-align: center;
	font-size: 120%;
	width: 0;
`;

interface RowProps {
	highlighted: boolean;
	group?: number;
}

const Row = styled.tr.attrs<RowProps>((props) => ({
	className:
		props.group == null ? undefined : props.group % 2 === 0 ? 'even' : 'odd'
}))<RowProps>`
	& td {
		color: ${(props) =>
			props.highlighted ? props.theme.colors.darkRed : undefined};
	}
	&.odd {
		background-color: ${(props) => props.theme.colors.veryLightBlue};
	}
	&.even {
	}
`;

const Table = styled.table`
	border-collapse: collapse;
	color: ${(props) => props.theme.colors.darkBlue};
	font: ${(props) => props.theme.font.default};
	font-size: 3.2vmin;
	font-weight: bold;
	width: 100%;

	& thead th {
		height: 2em;
		padding-top: 0.4em;
		padding-bottom: 0.4em;
		position: sticky;
		top: 0;
		background-color: ${(props) => props.theme.colors.darkBlue};
	}

	& thead {
		color: ${(props) => props.theme.colors.white};
	}
	& tbody {
		background-color: ${(props) => props.theme.colors.white};
	}

	& tbody td {
		padding-top: 0.1em;
		padding-bottom: 0.1em;
	}
`;

const Content = styled.div`
	overflow-y: auto;
`;

export interface Props {
	className?: string;
	entries: HighscoreEntry[];
	highlight: HighscoreEntry;
	maxEntryCount?: number;
}

const Highscore: FunctionComponent<Props> = (props: Props) => {
	const { className, entries, highlight, maxEntryCount = 10 } = props;

	const isHighlighted = useCallback(
		(entry: HighscoreEntry): boolean => isEqualHighscoreEntry(entry, highlight),
		[highlight]
	);

	const buildEntryComponent = useCallback(
		(
			entry: HighscoreEntry,
			index: number,
			group: number,
			rank?: number
		): ReactNode => {
			return (
				<Row key={index} group={group} highlighted={isHighlighted(entry)}>
					<Rank>{rank}</Rank>
					<Name>{entry.playerName}</Name>
					<PointsValue>{entry.score.qualityOfLife}</PointsValue>
					<CalculationSymbol>+</CalculationSymbol>
					<PointsValue>{entry.score.environment}</PointsValue>
					<CalculationSymbol>+</CalculationSymbol>
					<PointsValue>{entry.score.happiness}</PointsValue>
					<CalculationSymbol>=</CalculationSymbol>
					<SumPointsValue>{sumPoints(entry.score)}</SumPointsValue>
				</Row>
			);
		},
		[isHighlighted]
	);

	const entryComponents = useMemo(() => {
		let addHighlightToEnd = false;
		let displayEntries = slice(entries, 0, maxEntryCount);
		if (!some(displayEntries, isHighlighted)) {
			// the highlighted entry is not within the top entries
			// -> remove the last entry and add the highlighted one
			displayEntries = dropRight(displayEntries);
			addHighlightToEnd = true;
		}

		const entryComponents: ReactNode[] = [];
		let prevScoreSum: number | undefined = undefined;
		let index = 0;
		let rank = 0;
		for (const entry of displayEntries) {
			const scoreSum = sumPoints(entry.score);
			if (scoreSum !== prevScoreSum) {
				++rank;
			}
			entryComponents.push(buildEntryComponent(entry, index++, rank, rank));
			prevScoreSum = scoreSum;
		}

		if (addHighlightToEnd) {
			return [
				...entryComponents,
				buildEntryComponent(highlight, index, ++rank, undefined)
			];
		} else {
			return entryComponents;
		}
	}, [entries, maxEntryCount, buildEntryComponent, isHighlighted, highlight]);

	return (
		<Content className={className}>
			<Table>
				<thead>
					<tr key="header">
						<th colSpan={2}></th>
						<PointsType>
							<Image
								src={Assets.images.qualityOfLife}
								width="8vmin"
								height="8vmin"
							/>
						</PointsType>
						<th></th>
						<PointsType>
							<Image
								src={Assets.images.environment}
								width="8vmin"
								height="8vmin"
							/>
						</PointsType>
						<th></th>
						<PointsType>
							<Image
								src={Assets.images.happiness}
								width="8vmin"
								height="8vmin"
							/>
						</PointsType>
						<th></th>
						<PointsType />
					</tr>
				</thead>
				<tbody>{entryComponents}</tbody>
				<tfoot>
					<tr>
						<td colSpan={9}></td>
					</tr>
				</tfoot>
			</Table>
		</Content>
	);
};

export default styled(Highscore)<PositionAndSizeProps>(
	PositionAndSizeFromProps
);
